/**
 * The algos this UI can create, and the behaviour buttons that map onto them.
 *
 * Deliberately dependency-free. `Behavior` is consumed by the config form, the
 * shared types and three datagrids, and `configTab/types.tsx` reads
 * `Behavior.LIMIT_ORDER` at module scope — so if this module imported anything
 * that (even transitively) imported back, the enum could still be `undefined`
 * when that runs and the app would white-screen on load. Keep it a leaf.
 */

/** Sent as `algo_spec.name` when creating or updating an algo. */
export type AlgoSpecName =
  "limitorder" | "nurser" | "terminator" | "step_shimurai";

export const LIMIT_ORDER = "limitorder";
export const NURSER = "nurser";
export const TERMINATOR = "terminator";
export const STEP_SHIMURAI = "step_shimurai";

/** The behaviour buttons, in the order they appear in the config form. */
export enum Behavior {
  LIMIT_ORDER = "Limit Order",
  NURSER = "Nurser",
  TERMINATOR = "Terminator",
  STEP_SHIMURAI = "StepShimurai",
}

/**
 * How a terminator works its order, sent as `configuration.behaviors` — a
 * single value despite the plural key, as Skynet sends it. The values are the
 * wire strings.
 *
 * Not to be confused with {@link Behavior.NURSER}, which is a separate algo
 * (`nurser`); this one is a mode of the `terminator` algo.
 */
export enum TerminatorBehavior {
  NURSER = "Nurser",
  TOP_NURSER = "TopNurser",
  WAPPER = "Wapper",
  /**
   * Prices off a trader-set reference: sent with `price` equal to
   * `reference_price`, plus a margin override, a margin band and an update
   * frequency (see `toAlgo` in `configTab/index.tsx`).
   */
  LINEAR_CLOSER = "LinearCloser",
}

/**
 * Behaviours the menu no longer offers. Still members of
 * {@link TerminatorBehavior}, so a running terminator on one is recognised,
 * shows it as picked, and can be updated without being forced onto another.
 */
export const HIDDEN_TERMINATOR_BEHAVIORS: readonly TerminatorBehavior[] = [
  TerminatorBehavior.WAPPER,
];

/**
 * What a new terminator starts on, so selecting Terminator is submittable
 * straight away. The trader can still pick another from its menu.
 */
export const DEFAULT_TERMINATOR_BEHAVIOR = TerminatorBehavior.LINEAR_CLOSER;

/** Narrows `configuration.behaviors` off the wire to one this UI offers. */
export const isTerminatorBehavior = (
  value: unknown
): value is TerminatorBehavior =>
  Object.values(TerminatorBehavior).includes(value as TerminatorBehavior);

/**
 * Which algo each behaviour submits. Typed as a full `Record` so adding a
 * `Behavior` without giving it an algo fails to compile.
 */
export const behaviorAlgoSpec: Record<Behavior, AlgoSpecName> = {
  [Behavior.LIMIT_ORDER]: LIMIT_ORDER,
  [Behavior.NURSER]: NURSER,
  [Behavior.TERMINATOR]: TERMINATOR,
  [Behavior.STEP_SHIMURAI]: STEP_SHIMURAI,
};

/**
 * Inverse of {@link behaviorAlgoSpec}, for populating the form from an existing
 * algo. `undefined` means an algo this UI cannot edit.
 */
export const behaviorFromAlgoSpec = (name: string): Behavior | undefined =>
  (Object.keys(behaviorAlgoSpec) as Behavior[]).find(
    (behavior) => behaviorAlgoSpec[behavior] === name
  );

/**
 * The backend names shimurai variants `<algo>_shimurai`. The set is open — new
 * variants appear without this UI changing — so they are recognised by suffix
 * rather than by an exhaustive list. Only {@link STEP_SHIMURAI} has a
 * {@link Behavior} entry; the rest the desk can select and retune but not
 * create here.
 */
export const SHIMURAI_SUFFIX = "_shimurai";

/** Whether an `algo_spec.name` off the wire is a shimurai variant. */
export const isShimuraiAlgoSpec = (name: string): boolean =>
  name.endsWith(SHIMURAI_SUFFIX);
